import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Home, ShoppingCart, User, LogOut, Shield, Smartphone, Key, 
  Trash2, Monitor, Clock, CheckCircle, XCircle, RefreshCw, Copy, AlertTriangle
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function Admin2FA() {
  const { user, logout, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ enabled: false, setup_complete: false });
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [backupCodesCount, setBackupCodesCount] = useState(0);
  
  // Setup state
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // Disable state
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  
  // Regenerate backup codes state
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');

  useEffect(() => {
    fetchStatus();
    fetchTrustedDevices();
    fetchBackupCodesCount();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API}/api/admin/2fa/status`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrustedDevices = async () => {
    try {
      const response = await fetch(`${API}/api/admin/2fa/trusted-devices`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setTrustedDevices(data);
      }
    } catch (error) {
      console.error('Failed to fetch trusted devices:', error);
    }
  };

  const fetchBackupCodesCount = async () => {
    try {
      const response = await fetch(`${API}/api/admin/2fa/backup-codes-count`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setBackupCodesCount(data.count);
      }
    } catch (error) {
      console.error('Failed to fetch backup codes count:', error);
    }
  };

  const startSetup = async () => {
    try {
      const response = await fetch(`${API}/api/admin/2fa/setup`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start setup');
      }
    } catch (error) {
      toast.error('Failed to start 2FA setup');
    }
  };

  const verifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Enter a 6-digit code');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/admin/2fa/verify-setup`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verifyCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backup_codes);
        setShowBackupCodes(true);
        setSetupData(null);
        setVerifyCode('');
        fetchStatus();
        toast.success('2FA enabled successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to verify code');
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Enter a 6-digit code');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/admin/2fa/disable`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: disableCode })
      });
      
      if (response.ok) {
        setShowDisableModal(false);
        setDisableCode('');
        fetchStatus();
        fetchTrustedDevices();
        toast.success('2FA disabled');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to disable 2FA');
    }
  };

  const regenerateBackupCodes = async () => {
    if (!regenerateCode || regenerateCode.length !== 6) {
      toast.error('Enter a 6-digit code');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/admin/2fa/regenerate-backup-codes`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: regenerateCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backup_codes);
        setShowBackupCodes(true);
        setShowRegenerateModal(false);
        setRegenerateCode('');
        fetchBackupCodesCount();
        toast.success('Backup codes regenerated!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Failed to regenerate codes');
    }
  };

  const revokeDevice = async (deviceId) => {
    try {
      const response = await fetch(`${API}/api/admin/2fa/trusted-devices/${deviceId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        fetchTrustedDevices();
        toast.success('Device revoked');
      } else {
        toast.error('Failed to revoke device');
      }
    } catch (error) {
      toast.error('Failed to revoke device');
    }
  };

  const revokeAllDevices = async () => {
    if (!confirm('Revoke all trusted devices? You will need to verify 2FA on next login.')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/2fa/trusted-devices`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        fetchTrustedDevices();
        toast.success('All devices revoked');
      } else {
        toast.error('Failed to revoke devices');
      }
    } catch (error) {
      toast.error('Failed to revoke devices');
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="font-mono text-zinc-500">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#050505]/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/admin" className="text-zinc-500 hover:text-white transition-colors">
            <Shield className="w-5 h-5" />
          </Link>
          <span className="text-zinc-600">/</span>
          <h1 className="font-mono text-2xl text-white">TWO-FACTOR AUTHENTICATION</h1>
        </div>

        {/* Status Card */}
        <div className="bg-zinc-900 border border-zinc-700 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 flex items-center justify-center border ${status.enabled ? 'border-green-500 bg-green-500/10' : 'border-zinc-700'}`}>
                <Smartphone className={`w-6 h-6 ${status.enabled ? 'text-green-500' : 'text-zinc-500'}`} />
              </div>
              <div>
                <p className="font-mono text-lg text-white">GOOGLE AUTHENTICATOR</p>
                <p className="font-mono text-xs text-zinc-500">
                  {status.enabled ? 'ENABLED - Your account is protected' : 'DISABLED - Enable for extra security'}
                </p>
              </div>
            </div>
            
            {status.enabled ? (
              <Badge className="bg-green-500/20 text-green-400 border-0 font-mono">
                <CheckCircle className="w-3 h-3 mr-1" />
                ACTIVE
              </Badge>
            ) : (
              <Badge className="bg-zinc-700 text-zinc-400 border-0 font-mono">
                INACTIVE
              </Badge>
            )}
          </div>
          
          {!status.enabled && !setupData && (
            <Button
              onClick={startSetup}
              className="mt-6 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono"
            >
              ENABLE 2FA
            </Button>
          )}
          
          {status.enabled && (
            <Button
              onClick={() => setShowDisableModal(true)}
              variant="outline"
              className="mt-6 border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono"
            >
              DISABLE 2FA
            </Button>
          )}
        </div>

        {/* Setup Flow */}
        {setupData && (
          <div className="bg-zinc-900 border border-tiffany-500 p-6 mb-6">
            <h2 className="font-mono text-lg text-white mb-4">SETUP 2FA</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="font-mono text-xs text-zinc-500 mb-4">
                  1. SCAN THIS QR CODE WITH GOOGLE AUTHENTICATOR
                </p>
                <div className="bg-white p-4 inline-block">
                  <img src={setupData.qr_code} alt="QR Code" className="w-48 h-48" />
                </div>
                
                <p className="font-mono text-xs text-zinc-500 mt-4 mb-2">
                  OR ENTER THIS KEY MANUALLY:
                </p>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-xs bg-zinc-800 px-3 py-2 text-tiffany-500 break-all">
                    {setupData.manual_entry_key}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(setupData.manual_entry_key);
                      toast.success('Key copied!');
                    }}
                    className="p-2 text-zinc-500 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div>
                <p className="font-mono text-xs text-zinc-500 mb-4">
                  2. ENTER THE 6-DIGIT CODE FROM YOUR APP
                </p>
                <Input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full h-14 text-center text-2xl font-mono tracking-[0.5em] bg-zinc-800 border-zinc-700 text-white"
                  maxLength={6}
                />
                
                <Button
                  onClick={verifySetup}
                  disabled={verifyCode.length !== 6}
                  className="w-full mt-4 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono disabled:opacity-50"
                >
                  VERIFY & ENABLE
                </Button>
                
                <button
                  onClick={() => {
                    setSetupData(null);
                    setVerifyCode('');
                  }}
                  className="w-full mt-2 font-mono text-xs text-zinc-500 hover:text-white"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Backup Codes Display */}
        {showBackupCodes && backupCodes.length > 0 && (
          <div className="bg-zinc-900 border border-amber-500 p-6 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-mono text-lg text-white">SAVE YOUR BACKUP CODES</h2>
                <p className="font-mono text-xs text-zinc-500 mt-1">
                  Store these codes somewhere safe. Each can only be used once.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 bg-zinc-800 p-4 mb-4">
              {backupCodes.map((code, idx) => (
                <code key={idx} className="font-mono text-sm text-tiffany-500">{code}</code>
              ))}
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={copyBackupCodes}
                variant="outline"
                className="border-zinc-700 text-white hover:bg-zinc-800 font-mono"
              >
                <Copy className="w-4 h-4 mr-2" />
                COPY ALL
              </Button>
              <Button
                onClick={() => {
                  setShowBackupCodes(false);
                  setBackupCodes([]);
                }}
                className="bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono"
              >
                I'VE SAVED THEM
              </Button>
            </div>
          </div>
        )}

        {/* Backup Codes Management (when enabled) */}
        {status.enabled && (
          <div className="bg-zinc-900 border border-zinc-700 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-zinc-500" />
                <div>
                  <p className="font-mono text-white">BACKUP CODES</p>
                  <p className="font-mono text-xs text-zinc-500">{backupCodesCount} remaining</p>
                </div>
              </div>
              
              <Button
                onClick={() => setShowRegenerateModal(true)}
                variant="outline"
                className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 font-mono text-xs"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                REGENERATE
              </Button>
            </div>
            
            {backupCodesCount < 3 && (
              <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 p-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <p className="font-mono text-xs text-amber-400">
                  Low backup codes remaining. Consider regenerating.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Trusted Devices */}
        {status.enabled && (
          <div className="bg-zinc-900 border border-zinc-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Monitor className="w-5 h-5 text-zinc-500" />
                <div>
                  <p className="font-mono text-white">TRUSTED DEVICES</p>
                  <p className="font-mono text-xs text-zinc-500">Devices that skip 2FA for 30 days</p>
                </div>
              </div>
              
              {trustedDevices.length > 0 && (
                <Button
                  onClick={revokeAllDevices}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono text-xs"
                >
                  REVOKE ALL
                </Button>
              )}
            </div>
            
            {trustedDevices.length === 0 ? (
              <p className="font-mono text-xs text-zinc-600 text-center py-8">
                NO TRUSTED DEVICES
              </p>
            ) : (
              <div className="space-y-2">
                {trustedDevices.map((device) => (
                  <div 
                    key={device.id} 
                    className={`flex items-center justify-between p-3 border ${device.is_expired ? 'border-red-500/30 bg-red-500/5' : 'border-zinc-800'}`}
                  >
                    <div className="flex items-center gap-3">
                      <Monitor className={`w-5 h-5 ${device.is_expired ? 'text-red-500' : 'text-zinc-500'}`} />
                      <div>
                        <p className="font-mono text-sm text-white">{device.device_name}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="font-mono text-[10px] text-zinc-500">
                            <Clock className="w-3 h-3 inline mr-1" />
                            Last used: {formatDate(device.last_used)}
                          </span>
                          {device.is_expired && (
                            <Badge className="bg-red-500/20 text-red-400 border-0 font-mono text-[10px]">
                              EXPIRED
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => revokeDevice(device.id)}
                      className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Disable 2FA Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-md p-6">
            <h3 className="font-mono text-lg text-white mb-2">DISABLE 2FA</h3>
            <p className="font-mono text-xs text-zinc-500 mb-4">
              Enter your authenticator code to confirm.
            </p>
            
            <Input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full h-12 text-center text-xl font-mono tracking-[0.5em] bg-zinc-800 border-zinc-700 text-white mb-4"
              maxLength={6}
            />
            
            <div className="flex gap-3">
              <Button
                onClick={disable2FA}
                disabled={disableCode.length !== 6}
                className="flex-1 bg-red-500 text-white hover:bg-red-600 font-mono disabled:opacity-50"
              >
                DISABLE
              </Button>
              <Button
                onClick={() => {
                  setShowDisableModal(false);
                  setDisableCode('');
                }}
                variant="outline"
                className="flex-1 border-zinc-700 text-white hover:bg-zinc-800 font-mono"
              >
                CANCEL
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Backup Codes Modal */}
      {showRegenerateModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-md p-6">
            <h3 className="font-mono text-lg text-white mb-2">REGENERATE BACKUP CODES</h3>
            <p className="font-mono text-xs text-zinc-500 mb-4">
              This will invalidate all existing backup codes. Enter your authenticator code to confirm.
            </p>
            
            <Input
              type="text"
              value={regenerateCode}
              onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full h-12 text-center text-xl font-mono tracking-[0.5em] bg-zinc-800 border-zinc-700 text-white mb-4"
              maxLength={6}
            />
            
            <div className="flex gap-3">
              <Button
                onClick={regenerateBackupCodes}
                disabled={regenerateCode.length !== 6}
                className="flex-1 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono disabled:opacity-50"
              >
                REGENERATE
              </Button>
              <Button
                onClick={() => {
                  setShowRegenerateModal(false);
                  setRegenerateCode('');
                }}
                variant="outline"
                className="flex-1 border-zinc-700 text-white hover:bg-zinc-800 font-mono"
              >
                CANCEL
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
